import React, { useState } from 'react'
import { 
  X, 
  Gift, 
  Loader, 
  CheckCircle, 
  AlertTriangle,
  CreditCard,
  Tag,
  Calendar,
  Users
} from 'lucide-react'
import { apiClient } from '@/lib/api'

interface CouponData {
  id: number
  uuid: string
  code: string
  coupon_type: string
  description: string
  discount_type: string
  discount_value: number
  credit: number
  effective_date: string
  expiry_date: string
  max_usage: number | null
  per_user_limit: number
  status: string
  total_usage: number
  user_usage: number
}

interface CouponRedemptionModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (creditsAdded: number) => void
}

export default function CouponRedemptionModal({ 
  isOpen, 
  onClose, 
  onSuccess 
}: CouponRedemptionModalProps) {
  const [couponCode, setCouponCode] = useState('')
  const [validating, setValidating] = useState(false)
  const [redeeming, setRedeeming] = useState(false)
  const [validatedCoupon, setValidatedCoupon] = useState<CouponData | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [redemptionResult, setRedemptionResult] = useState<{
    success: boolean
    creditsAdded: number
    message: string
  } | null>(null)

  const handleValidate = async () => {
    if (!couponCode.trim()) {
      setValidationError('Please enter a coupon code')
      return
    }

    setValidating(true)
    setValidationError(null)
    setValidatedCoupon(null)

    try {
      const response = await apiClient.request('/coupons/validate', {
        method: 'POST',
        body: JSON.stringify({ code: couponCode.trim().toUpperCase() })
      })

      if (response.success && response.data?.valid) {
        setValidatedCoupon(response.data.coupon)
      } else {
        setValidationError(response.data?.error_message || response.error || 'Invalid coupon code')
      }
    } catch (err) {
      setValidationError('Failed to validate coupon code')
    } finally {
      setValidating(false)
    }
  }

  const handleRedeem = async () => {
    if (!validatedCoupon) return

    setRedeeming(true)
    setRedemptionResult(null)

    try {
      const response = await apiClient.request('/coupons/redeem', {
        method: 'POST',
        body: JSON.stringify({ code: couponCode.trim().toUpperCase() })
      })

      if (response.success && response.data) {
        const result = {
          success: true,
          creditsAdded: response.data.credits_added || 0,
          message: response.data.message || 'Coupon redeemed successfully!'
        }
        setRedemptionResult(result)
        
        // Call success callback
        if (onSuccess && result.creditsAdded > 0) {
          onSuccess(result.creditsAdded)
        }
      } else {
        setRedemptionResult({
          success: false,
          creditsAdded: 0,
          message: response.data?.message || response.error || 'Failed to redeem coupon'
        })
      }
    } catch (err) {
      setRedemptionResult({
        success: false,
        creditsAdded: 0,
        message: 'Failed to redeem coupon'
      })
    } finally {
      setRedeeming(false)
    }
  }

  const handleClose = () => {
    setCouponCode('')
    setValidatedCoupon(null)
    setValidationError(null)
    setRedemptionResult(null)
    onClose()
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'Invalid date'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Gift className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Redeem Coupon</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {!redemptionResult ? (
            <>
              {/* Coupon Code Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon Code
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Enter coupon code"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                      disabled={validating || redeeming}
                    />
                    <button
                      onClick={handleValidate}
                      disabled={validating || redeeming || !couponCode.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {validating ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        'Validate'
                      )}
                    </button>
                  </div>
                </div>

                {/* Validation Error */}
                {validationError && (
                  <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-800">{validationError}</span>
                  </div>
                )}

                {/* Validated Coupon Info */}
                {validatedCoupon && (
                  <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-900">Valid Coupon!</span>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Code:</span>
                        <span className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                          {validatedCoupon.code}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Description:</span>
                        <span className="text-sm text-gray-900 text-right max-w-48">
                          {validatedCoupon.description}
                        </span>
                      </div>

                      {validatedCoupon.coupon_type === 'credit' && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            <CreditCard className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-700">Credits:</span>
                          </div>
                          <span className="text-lg font-bold text-blue-600">
                            +{validatedCoupon.credit}
                          </span>
                        </div>
                      )}

                      {validatedCoupon.coupon_type === 'discount' && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            <Tag className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-medium text-gray-700">Discount:</span>
                          </div>
                          <span className="text-lg font-bold text-purple-600">
                            {validatedCoupon.discount_value}%
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Expires:</span>
                        </div>
                        <span className="text-sm text-gray-900">
                          {formatDate(validatedCoupon.expiry_date)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <Users className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Usage:</span>
                        </div>
                        <span className="text-sm text-gray-900">
                          {validatedCoupon.user_usage} / {validatedCoupon.per_user_limit}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={handleRedeem}
                      disabled={redeeming}
                      className="w-full mt-4 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {redeeming ? (
                        <div className="flex items-center justify-center space-x-2">
                          <Loader className="w-4 h-4 animate-spin" />
                          <span>Redeeming...</span>
                        </div>
                      ) : (
                        `Redeem for ${validatedCoupon.credit} Credits`
                      )}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Redemption Result */
            <div className="text-center space-y-4">
              {redemptionResult.success ? (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Coupon Redeemed Successfully!
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {redemptionResult.message}
                    </p>
                    {redemptionResult.creditsAdded > 0 && (
                      <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-100 rounded-full">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-900">
                          +{redemptionResult.creditsAdded} Credits Added
                        </span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Redemption Failed
                    </h3>
                    <p className="text-gray-600">
                      {redemptionResult.message}
                    </p>
                  </div>
                </>
              )}
              
              <button
                onClick={handleClose}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}